import { Rng } from '@/core/rng';
import { type LootStack, lootTableFor, rollCountFor, rollLoot } from '@/data/loot';
import { type Floor, centreOf } from '@/dungeon/generator';

/**
 * Chests on a floor.
 *
 * A layer over the map rather than tiles in it, which is how farmland and
 * furniture already work. Keeping them out of the tilemap means a floor stays
 * exactly what its seed says it is, and opening one is a change to this layer
 * rather than a mutation of terrain.
 *
 * Placed from a stream salted apart from the rooms, which is what those salts
 * were for: adding a chest roll cannot move a single wall.
 *
 * Not saved, like everything else down here. A floor is regenerated on entry,
 * so a chest you left closed will be a different chest next time — that is the
 * cost of floors being disposable, and the reason to open what you find.
 */

/** Placement stream. Rooms use 0x1; this is deliberately separate. */
const CHEST_SALT = 0x2;

/** Contents stream, so re-rolling placement cannot reshuffle what is inside. */
const CONTENTS_SALT = 0x3;

export interface Chest {
  readonly x: number;
  readonly y: number;
  opened: boolean;
}

export class Chests {
  private readonly byTile = new Map<string, Chest>();

  private constructor(
    private readonly seed: number,
    private readonly depth: number,
  ) {}

  /**
   * Scatter chests through the rooms of a floor.
   *
   * Never in the first or last room: one holds the way back up and the other
   * the way down, and a reward placed on the exit is a reward for leaving.
   */
  static forFloor(seed: number, floor: Floor): Chests {
    const chests = new Chests(seed, floor.depth);
    const rng = Rng.forStream(seed, floor.depth, CHEST_SALT);

    const middle = floor.rooms.slice(1, -1);
    if (middle.length === 0) return chests;

    // Roughly half the eligible rooms, always at least one, so a floor is
    // never a walk for nothing.
    const wanted = Math.max(1, Math.round(middle.length / 2));

    for (const room of rng.shuffle(middle).slice(0, wanted)) {
      // Off-centre, so a chest is something you walk over to rather than
      // something you trip on crossing the room.
      const x = rng.int(room.x, room.x + room.width - 1);
      const y = rng.int(room.y, room.y + room.height - 1);
      const centre = centreOf(room);
      if (x === centre.x && y === centre.y && middle.length > 1) continue;

      chests.byTile.set(`${x},${y}`, { x, y, opened: false });
    }

    return chests;
  }

  all(): Chest[] {
    return [...this.byTile.values()];
  }

  at(tileX: number, tileY: number): Chest | null {
    return this.byTile.get(`${tileX},${tileY}`) ?? null;
  }

  get unopened(): number {
    return this.all().filter((chest) => !chest.opened).length;
  }

  /**
   * Open one, returning what was inside.
   *
   * Contents are rolled from the tile it stands on, so the same chest on the
   * same floor always holds the same thing — opening, closing the game and
   * opening again is not a way to reroll a sword.
   */
  open(tileX: number, tileY: number): LootStack[] | null {
    const chest = this.at(tileX, tileY);
    if (!chest || chest.opened) return null;

    chest.opened = true;

    const rng = Rng.forStream(
      this.seed,
      // Mixing the tile into the index keeps two chests on one floor distinct.
      this.depth * 10_000 + tileY * 100 + tileX,
      CONTENTS_SALT,
    );

    return rollLoot(rng, lootTableFor(this.depth), rollCountFor(this.depth));
  }
}
